import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { api, type ApiError, type OpenPublicEventResult } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { Button } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-67 Event registration page (F-083).
 *
 * `/e/<workspace>/<event>`: what, when and where, and the registration form.
 * A full event still takes registrations - they wait for a place - and the
 * page says so before anybody fills anything in. A webinar's join link is not
 * on the page; it goes to the people who registered, in their reminders.
 */

function trackingParameters(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.startsWith('utm_') || key === 'gclid' || key === 'fbclid') out[key] = value;
  }
  return out;
}

function EventRegistrationScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const params = useParams();
  const location = useLocation();
  const workspace = params['workspace'] ?? '';
  const slug = params['slug'] ?? '';
  const opened = useRef(Date.now());
  const [values, setValues] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);

  const page = useQuery<OpenPublicEventResult, ApiError>({
    queryKey: ['public-event', workspace, slug],
    queryFn: () => api.openPublicEvent({ path: { workspace, slug } }),
    retry: false,
  });
  const register = useMutation({
    mutationFn: () =>
      api.registerForEvent({
        path: { workspace, slug },
        body: {
          values,
          consent,
          utm: trackingParameters(location.search),
          referrer: document.referrer || null,
          filledInSeconds: Math.round((Date.now() - opened.current) / 1000),
        },
      }),
  });

  if (page.isPending) return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
  if (page.isError || !page.data.event) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-2 px-gutter py-6">
        <h1 data-event-missing className="text-h2 text-ink">{t('events.missing')}</h1>
        {page.isError ? <p className="text-body text-ink-meta">{errorText(page.error)}</p> : null}
      </main>
    );
  }
  const event = page.data.event;
  const outcome = register.data;

  return (
    <main data-component="EventRegistration" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-4 px-gutter py-6">
      <p className="text-meta text-ink-meta">{t(`events.kind.${event.kind}`)}</p>
      <h1 className="text-h1 text-ink">{event.name}</h1>
      <p data-event-when className="text-body text-ink">
        {formatDateTime(event.startsAt, format)} – {formatDateTime(event.endsAt, format)}
      </p>
      {event.location ? <p className="text-body text-ink-600">{event.location}</p> : null}
      {event.description ? <p className="whitespace-pre-wrap text-body text-ink">{event.description}</p> : null}

      {!event.open ? (
        <p data-event-registration-closed className="text-body text-ink-meta">{t('events.registrationClosed')}</p>
      ) : outcome?.accepted && outcome.registration ? (
        <p data-event-registered={outcome.registration} className="rounded-card border border-rule p-4 text-body text-ink">
          {outcome.registration === 'waitlisted'
            ? t('events.youAreWaitlisted')
            : outcome.registration === 'already-registered'
              ? t('events.alreadyRegistered')
              : t('events.youAreRegistered')}
        </p>
      ) : event.form ? (
        <section data-event-form className="flex flex-col gap-3 rounded-card border border-rule p-4">
          {event.full ? <p data-event-full className="text-dense text-warning-text">{t('events.fullWaitlist')}</p> : null}
          {event.form.fields.map((field) => (
            <label key={field.field} className="flex flex-col gap-1 text-dense text-ink-600">
              {field.label}
              <input
                data-event-field={field.field}
                required={field.required}
                type={field.field === 'email' ? 'email' : 'text'}
                className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink"
                value={values[field.field] ?? ''}
                onChange={(e) => setValues((current) => ({ ...current, [field.field]: e.target.value }))}
              />
            </label>
          ))}
          {event.form.consentRequired && event.form.consentText ? (
            <label className="flex items-start gap-2 text-dense text-ink">
              <input type="checkbox" data-event-consent checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>{event.form.consentText}</span>
            </label>
          ) : null}
          {outcome && !outcome.accepted ? (
            <p data-event-register-refusal className="text-dense text-danger-text">
              {outcome.messageKey ? errorText({ message: outcome.message, messageKey: outcome.messageKey }) : outcome.message}
            </p>
          ) : null}
          <div>
            <Button intent="primary" data-event-register disabled={register.isPending} onClick={() => register.mutate()}>
              {t('events.register')}
            </Button>
          </div>
        </section>
      ) : null}
    </main>
  );
}

function RegistrationLoading(): JSX.Element {
  const t = useT();
  return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
}
function RegistrationMissing(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-ink-meta">{t('events.missing')}</p>;
}

export const screen = defineScreen({
  id: 'SC-67',
  route: '/e/:workspace/:slug',
  surface: 'page',
  chrome: 'bare',
  title: 'Event registration',
  features: ['F-083'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: [],
  elements: ['EventRegistration'],
  sampleRecord: [
    { table: { name: 'tenant', idColumn: 'slug' }, param: 'workspace' },
    { table: { name: 'marketing_event', idColumn: 'slug' }, param: 'slug' },
  ],
  states: { empty: RegistrationMissing, loading: RegistrationLoading, error: RegistrationMissing },
  component: EventRegistrationScreen,
});

export default screen;
